// src/admin/legal/CorrectVersionModal.jsx — «Correggi» (v219): the links and/or the PDF of a version the BE still
// calls `correctable` (not yet in effect, or accepted by nobody). Document, version and date cannot change.
// ⛔ Sends ONLY what differs from the loaded row (legalModel.correctionChanges); nothing changed → «Salva» disabled,
// so LEGAL_CORRECTION_EMPTY is unreachable. An emptied link is a change and fails the https check.
// ⛔ No confirmation (the handoff asks for none). 409 LEGAL_VERSION_FROZEN / 404 gone → our sentence + re-read
// (onStale). Field errors land on their field. Never err.message.
// ⚠️ closeOnOverlay={false} and mounted conditionally by the page, as AddVersionModal.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdminModal from '../components/AdminModal.jsx';
import { correctLegalVersion } from '../legalDocumentsApi.js';
import { correctionChanges, validateCorrection, sniffPdf, formatDateTime, documentLabelKey } from './legalModel.js';
import { legalErrorCopy } from './legalErrorCopy.js';
import { LinkFields, PdfField } from './legalFields.jsx';
import { alertStyle, buttonStyle, hintStyle, fieldStyle } from './legalFormStyles.js';

export default function CorrectVersionModal({ row, onClose, onCorrected, onStale }) {
  const { t, i18n } = useTranslation('admin');
  const [form, setForm] = useState({ url: row.url || '', pdfUrl: row.pdf_url || '', file: null });
  const [problems, setProblems] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const changes = correctionChanges(form, row);
  const hasChanges = Object.keys(changes).length > 0;
  // Same rule as AddVersionModal.problemText: a copy key from legalErrorCopy, else a model problem code.
  const problemText = (field) => {
    const p = problems[field];
    if (!p) return '';
    return p.startsWith('legalDocuments.') || p.startsWith('actionErrors.') ? t(p) : t(`legalDocuments.problems.${p}`);
  };
  const docName = documentLabelKey(row.document) ? t(documentLabelKey(row.document)) : row.document;

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    const found = validateCorrection(changes);
    setProblems(found);
    if (Object.keys(found).length > 0 || !hasChanges) return;
    setBusy(true);
    try {
      if (changes.file && !(await sniffPdf(changes.file))) { setProblems({ file: 'pdfType' }); return; }
      const updated = await correctLegalVersion(row.document, row.version, changes);
      await onCorrected(updated);
    } catch (err) {
      const copy = legalErrorCopy(err);
      if (copy.field) setProblems({ [copy.field]: copy.key });
      else setError(t(copy.key, copy.params));
      if (copy.reload) onStale();
    } finally {
      setBusy(false);
    }
  }

  const blocked = !hasChanges || busy;
  return (
    <AdminModal
      open onClose={onClose} title={t('legalDocuments.correctTitle', { version: row.version })}
      width={560} closeOnOverlay={false}
    >
      <form onSubmit={handleSave} noValidate>
        <p style={{ ...hintStyle, ...fieldStyle, fontSize: '13px' }}>
          {t('legalDocuments.form.fixed', {
            document: docName, version: row.version, date: formatDateTime(row.effective_from, i18n.language),
          })}
        </p>
        {error && <div role="alert" style={alertStyle}>{error}</div>}
        <LinkFields
          url={form.url} pdfUrl={form.pdfUrl} onUrl={set('url')} onPdfUrl={set('pdfUrl')}
          problems={{ url: problemText('url'), pdfUrl: problemText('pdfUrl'), links: problemText('links') }}
        />
        <PdfField
          file={form.file} onFile={set('file')} hintKey="legalDocuments.form.fileReplaceHint"
          problem={problemText('file')}
        />
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={buttonStyle(false)}>{t('common:actions.cancel')}</button>
          <button type="submit" disabled={blocked} style={buttonStyle(true, blocked)}>
            {busy ? t('legalDocuments.form.saving') : t('legalDocuments.form.save')}
          </button>
        </div>
      </form>
    </AdminModal>
  );
}
